import { useCallback, useEffect, useMemo, useState } from 'react'

import { useBucket } from '@mc/experiments-react'
import { trackExperimentViewed, getPageSource } from '@mc/track-event'

import {
  CATEGORY_TAGGED_PAGE_SOURCES,
  FOR_YOU_EXP_FLAG,
  FOR_YOU_INTEREST_UPDATED_EVENT,
  NUDGE_SHOW_DELAY_MS,
} from './constants'
import {
  getForYouInterest,
  getForYouInterestCategoryId,
  setBrowseNudgeDismissed,
  setForYouInterestIfUnset,
} from './utils'

const useIsCategoryTaggedPage = () => {
  const currentPageSource = getPageSource()
  return useMemo(
    () =>
      currentPageSource &&
      CATEGORY_TAGGED_PAGE_SOURCES.includes(currentPageSource),
    [currentPageSource],
  )
}

export const useSetForYouInterest = ({
  categoryName,
  categoryId,
}: {
  categoryName?: string
  categoryId?: string
}) => {
  const bucket = useBucket(FOR_YOU_EXP_FLAG)
  const isCategoryTaggedPage = useIsCategoryTaggedPage()
  const isVariant = isCategoryTaggedPage && bucket === 'variant_1'

  const [hasScrolled, setHasScrolled] = useState(false)

  const handleScroll = useCallback(() => {
    setHasScrolled(true)
    setTimeout(() => {
      trackExperimentViewed(FOR_YOU_EXP_FLAG)
      if (isVariant) {
        setForYouInterestIfUnset({ categoryName, categoryId })
      }
    }, NUDGE_SHOW_DELAY_MS)
  }, [categoryId, categoryName, isVariant])

  useEffect(() => {
    if (isCategoryTaggedPage && !hasScrolled) {
      window.addEventListener('scroll', handleScroll)

      return () => {
        window.removeEventListener('scroll', handleScroll)
      }
    }

    return undefined
  }, [
    bucket,
    categoryId,
    categoryName,
    handleScroll,
    hasScrolled,
    isCategoryTaggedPage,
  ])
}

export const useForYouInterestUpdated = (handler: () => void) => {
  useEffect(() => {
    // Initial client-side render
    handler()

    window.addEventListener(FOR_YOU_INTEREST_UPDATED_EVENT, handler)

    return () => {
      window.removeEventListener(FOR_YOU_INTEREST_UPDATED_EVENT, handler)
    }
  }, [handler])
}

export const useForYouBrowseNudge = () => {
  const bucket = useBucket(FOR_YOU_EXP_FLAG)
  const isVariant = bucket === 'variant_1'
  const [showNudge, setShowNudge] = useState<boolean>(false)

  const setNudgeDismissed = useCallback(() => {
    if (isVariant) {
      setBrowseNudgeDismissed()
    }
  }, [isVariant])

  const handleForYouInterestUpdated = useCallback(() => {
    const forYouInterest = getForYouInterest()
    if (forYouInterest) {
      setShowNudge(
        !!(
          isVariant &&
          forYouInterest.categoryId &&
          !forYouInterest.browseNudgeDismissed
        ),
      )
    }
  }, [isVariant])

  useForYouInterestUpdated(handleForYouInterestUpdated)

  return {
    setNudgeDismissed,
    showNudge,
  }
}

export const useForYouInNavExperiment = () => {
  const bucket = useBucket(FOR_YOU_EXP_FLAG)
  const isVariant = bucket === 'variant_1'

  const [interestCategoryId, setInterestCategoryId] = useState<string | null>()

  const handleForYouInterestUpdated = useCallback(() => {
    setInterestCategoryId(getForYouInterestCategoryId())
  }, [])

  useForYouInterestUpdated(handleForYouInterestUpdated)

  return { interestCategoryId: isVariant ? interestCategoryId : null }
}
